import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { Redux } from '../..'
import { JobStatus } from '@/src/types/apps/operations'

export interface Category {
  id: number
  name: string
  posMenuId: number
  posPlu: number
  description: string
  extras: string
  fillings: string
  posName: string
  posId: string
  posMenu: {
    id: number
    name: string
  }
}

interface CategoriesInitialState {
  categories?: Category[] | []
  category?: Category | null
  loading: boolean
  success: string | null
  error: string | null
  jobId?: number
  jobStatus?: JobStatus
  isDialogOpen?: boolean
  meta?: {
    totalRows: number
    pages: number
  }
}

const initialState: CategoriesInitialState = {
  categories: [],
  loading: false,
  success: null,
  error: null,
  jobId: undefined,
  isDialogOpen: false,
  category: null
}

interface CategoriesResponse {
  data: Category[]
  meta?: {
    totalRows: number
    pages: number
  }
}

export interface ToggleStatusPayload {
  isActive: number
  categoryIds: number[]
}

export const getCategories = createAsyncThunk<CategoriesResponse, any, Redux>(
  'gygCategories/getAllCategories',
  async (gygQueryOptions, { extra }) => {
    delete extra.httpClient.defaults.headers.Authorization
    const { data: responseData } = await extra.httpClient.get(`categories`, { params: gygQueryOptions })

    return responseData
  }
)

export const getCategory = createAsyncThunk<Category, number, Redux>(
  'gygCategories/getCategory',
  async (id, { extra }) => {
    delete extra.httpClient.defaults.headers.Authorization
    const { data: responseData } = await extra.httpClient.get(`categories/${id}`)

    return responseData
  }
)

export const toggleStatus = createAsyncThunk<any, ToggleStatusPayload, Redux>(
  'gygMenuContainerProducts/patchMenuContainerProduct',
  async (payload, { extra }) => {
    const { data: responseData } = await extra.httpClient.put(`categories/toggle-status`, payload)

    return responseData
  }
)

export const createCategory = createAsyncThunk<Category, Partial<Category>, Redux>(
  'gygCategories/createCategory',
  async (newCategory, { extra }) => {
    const { data: responseData } = await extra.httpClient.post(`categories`, newCategory)

    return responseData
  }
)

export const updateCategory = createAsyncThunk<Category, Partial<Category>, Redux>(
  'gygCategories/updateCategory',
  async (updatedCategory, { extra }) => {
    const { data: responseData } = await extra.httpClient.put(`categories/${updatedCategory.id}`, updatedCategory)

    return responseData
  }
)

const gygCategorySlice = createSlice({
  name: 'getCategories',
  initialState,
  reducers: {
    setIsDialogOpen: (state, action) => {
      state.isDialogOpen = action.payload
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getCategories.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getCategories.fulfilled, (state, action) => {
        state.loading = false
        state.success = null
        state.categories = action.payload.data
        state.meta = action.payload.meta
      })
      .addCase(getCategories.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to fetch pos menu configuration'
      })
      .addCase(getCategory.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getCategory.fulfilled, (state, action) => {
        state.loading = false
        state.success = null
        state.category = action.payload
      })
      .addCase(getCategory.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to fetch pos menu configuration'
      })
      .addCase(toggleStatus.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(toggleStatus.fulfilled, state => {
        state.loading = false
        state.success = 'status updated'
      })
      .addCase(toggleStatus.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to fetch pos menu configuration'
      })
  }
})
export const { setIsDialogOpen } = gygCategorySlice.actions

export default gygCategorySlice.reducer
